import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getHeightsConfig, putBeamHeight, putLevelHeights, putNominalHeights } from "../api/rest";
import css from "./WarehouseAdminPage.module.css";

/** Parse a pt-BR or en-US decimal ("2,2" / "2.2"); null for blank, NaN for junk. */
function parseMetres(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Metres for display, decimal comma (pt-BR default, per the brand rules). */
function fmt(n: number | null | undefined, locale: string): string {
  if (n === null || n === undefined) return "";
  return n.toLocaleString(locale, { maximumFractionDigits: 2 });
}

/**
 * ADR 226: the warehouse's nominal level height, plus the per-level table.
 *
 * Sits beside the per-address heights import because it is the case that actually
 * happens: most warehouses never produce a "Tamanho do Local" file, but everyone
 * knows how tall a level is. Empty means "use the platform default", and the field
 * shows that default as its placeholder so blank never reads as unknown.
 *
 * The per-level table is the second tier — "level 1 is taller than the rest" is a
 * common rack design, and it is one row per level rather than 15.000 rows per
 * address. Elevations stack server-side from the clear heights.
 */
export function NominalHeightField({ siteId, warehouseId }: { siteId: string; warehouseId: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [clear, setClear] = useState("");
  const [beam, setBeam] = useState("");
  const [perLevel, setPerLevel] = useState<Record<number, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["heights-config", siteId, warehouseId],
    queryFn: () => getHeightsConfig(siteId, warehouseId),
  });

  // Seed the inputs ONCE, when the server state first arrives — and never after the
  // operator has typed. Re-seeding on every `data` identity change would wipe input
  // the moment a refetch landed, and even the FIRST seed must not fire late and eat
  // what was typed while the query was still in flight.
  const seeded = useRef(false);
  const touch = () => { seeded.current = true; };
  useEffect(() => {
    if (!data || seeded.current) return;
    seeded.current = true;
    setClear(fmt(data.nominal.clearHeightM, i18n.language));
    setBeam(fmt(data.nominal.beamHeightM, i18n.language));
    setPerLevel(Object.fromEntries(
      data.levels.map((l) => [l.level, fmt(l.clearHeightM, i18n.language)]),
    ));
  }, [data, i18n.language]);

  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: ["heights-config", siteId, warehouseId] }),
    // The rack prisms are recomputed from these numbers.
    qc.invalidateQueries({ queryKey: ["structure-summary", siteId, warehouseId] }),
  ]);

  const saveNominal = useMutation({
    mutationFn: (clearHeightM: number | null) =>
      putNominalHeights(siteId, warehouseId, { clearHeightM, levelPitchM: null }),
    onSuccess: invalidate,
  });

  const saveLevels = useMutation({
    mutationFn: (levels: { level: number; clearHeightM: number | null; elevationM: null }[]) =>
      putLevelHeights(siteId, warehouseId, { levels }),
    onSuccess: invalidate,
  });

  const saveBeam = useMutation({
    mutationFn: (beamHeightM: number | null) => putBeamHeight(siteId, warehouseId, beamHeightM),
    onSuccess: invalidate,
  });

  function commitNominal() {
    const v = parseMetres(clear);
    if (v === undefined) { setErr(t("structure.nominal.invalid")); return; }
    setErr(null);
    saveNominal.mutate(v);
  }

  function commitBeam() {
    // Unlike the clear height, zero is a legitimate beam ("no beam" shelving).
    const trimmed = beam.trim();
    const v = trimmed === "" ? null : Number(trimmed.replace(",", "."));
    if (v !== null && (!Number.isFinite(v) || v < 0 || v >= 1)) {
      setErr(t("structure.beam.invalid")); return;
    }
    setErr(null);
    saveBeam.mutate(v);
  }

  function commitLevels() {
    const levels: { level: number; clearHeightM: number | null; elevationM: null }[] = [];
    for (const [lv, raw] of Object.entries(perLevel)) {
      const v = parseMetres(raw);
      if (v === undefined) { setErr(t("structure.nominal.invalid")); return; }
      if (v !== null) levels.push({ level: Number(lv), clearHeightM: v, elevationM: null });
    }
    if (levels.length === 0) { setErr(t("structure.nominal.noLevels")); return; }
    setErr(null);
    saveLevels.mutate(levels);
  }

  const placeholder = data ? fmt(data.nominal.defaultClearHeightM, i18n.language) : "";

  return (
    <div className={css.nominal}>
      <div className={css.field}>
        <label className={css.label} htmlFor="nominal-clear">{t("structure.nominal.label")}</label>
        <div className={css.nominalRow}>
          <input
            id="nominal-clear"
            className={css.inputNarrow}
            inputMode="decimal"
            value={clear}
            placeholder={placeholder}
            onChange={(e) => { touch(); setClear(e.target.value); }}
            onBlur={commitNominal}
            aria-describedby="nominal-help"
          />
          <span className={css.unit}>m</span>
          {/* Named, not a bare "Salvar": the structure editor further down the page
              has its own save, and two identically-labelled buttons is a trap. */}
          <button type="button" className={css.linkBtn} disabled={saveNominal.isPending}
                  onClick={commitNominal}>
            {saveNominal.isPending ? t("common.saving") : t("structure.nominal.save")}
          </button>
        </div>
        <p className={css.note} id="nominal-help">
          {t("structure.nominal.help", { value: placeholder })}
        </p>
      </div>

      {/* ADR 245: the beam between levels. The heights file speaks clear openings
          (beam-to-beam), so stacked elevations need this added per level — saving
          re-shifts every stored elevation to embed the new value. */}
      <div className={css.field}>
        <label className={css.label} htmlFor="beam-height">{t("structure.beam.label")}</label>
        <div className={css.nominalRow}>
          <input
            id="beam-height"
            className={css.inputNarrow}
            inputMode="decimal"
            value={beam}
            placeholder={data ? fmt(data.nominal.defaultBeamHeightM, i18n.language) : ""}
            onChange={(e) => { touch(); setBeam(e.target.value); }}
            aria-describedby="beam-help"
          />
          <span className={css.unit}>m</span>
          <button type="button" className={css.linkBtn} disabled={saveBeam.isPending}
                  onClick={commitBeam}>
            {saveBeam.isPending ? t("common.saving") : t("structure.beam.save")}
          </button>
          {saveBeam.isSuccess && (
            <span className={css.progress} role="status">
              {t("structure.beam.applied", { count: saveBeam.data.shifted })}
            </span>
          )}
        </div>
        <p className={css.note} id="beam-help">{t("structure.beam.help")}</p>
      </div>

      {(data?.levels.length ?? 0) > 0 && (
        <button type="button" className={css.linkBtn} onClick={() => setOpen((v) => !v)}>
          {t("structure.nominal.perLevel")}
        </button>
      )}

      {open && data && (
        <div className={css.nominalLevels}>
          <p className={css.note}>{t("structure.nominal.perLevelHelp")}</p>
          {data.levels.map((l) => (
            <div key={l.level} className={css.nominalRow}>
              <span className={css.label}>{t("structure.nominal.level", { level: l.level })}</span>
              <input
                className={css.inputNarrow}
                inputMode="decimal"
                value={perLevel[l.level] ?? ""}
                placeholder={placeholder}
                onChange={(e) => { touch(); setPerLevel((p) => ({ ...p, [l.level]: e.target.value })); }}
                aria-label={t("structure.nominal.level", { level: l.level })}
              />
              <span className={css.unit}>m</span>
              <span className={css.note}>{t("structure.nominal.levelNodes", { count: l.nodes })}</span>
            </div>
          ))}
          <button type="button" className={css.linkBtn} disabled={saveLevels.isPending}
                  onClick={commitLevels}>
            {saveLevels.isPending ? t("common.saving") : t("structure.nominal.applyAll")}
          </button>
          {saveLevels.isSuccess && (
            <span className={css.progress} role="status">
              {t("structure.nominal.applied", {
                levels: saveLevels.data.levelsUpdated, aisles: saveLevels.data.aislesTouched,
              })}
            </span>
          )}
        </div>
      )}

      {err && <p className={css.resultError} role="alert">{err}</p>}
    </div>
  );
}
